import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import evosLogo from '../../../assets/EvosLogo.svg';
import { QrIcon } from '../../../assets/Qr';
import { XIcon } from '../../../assets/X';
import { custonSooner } from '../../../components/CustopmSooner';
import { ProductImage } from '../../../components/ProductImage';
import { Button } from '../../../components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from '../../../components/ui/drawer';
import { impactOccuer } from '../../../utils/hapticEvents';
import { PromotionCountdown } from '../../home/ui/component/PromotionCountdown';
import { PromoMarkdown } from './PromoMarkdown';
import { PromotionDeal } from '../types/Deals';

/**
 * Кнопки «В корзину» и QR у акции пока скрыты — и на карточке, и в шторке.
 * Вернуть — поставить true.
 */
const SHOW_DEAL_ACTIONS = false;

type DealCardProps = {
  deal: PromotionDeal;
};

type DrawerHeaderProps = {
  title: string;
  onClose: () => void;
};

const DrawerHeader = ({ title, onClose }: DrawerHeaderProps) => (
  <div className="flex flex-row items-center justify-between px-4 pb-2 pt-3">
    <DrawerTitle className="text-display-md text-text-primary">
      {title}
    </DrawerTitle>
    <button
      type="button"
      className="flex h-6 w-6 items-center justify-center rounded-full bg-button-secondary"
      onClick={onClose}
    >
      <XIcon className="text-text-icon-secondary" />
    </button>
  </div>
);

/** Карточка акции (макет 38-3962) и шторка с подробностями. */
export const DealCard = ({ deal }: DealCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Язык уже выбран бэкендом (см. dealsListAdapter) — берём как пришло.
  const { title, description, fullDescription } = deal;
  const imageUrl = deal.imageUrl || deal.img;
  const [openDetails, setOpenDetails] = useState(false);
  const [openQr, setOpenQr] = useState(false);

  // Поведение прежнее: товары акции пока не добавляются, только переход
  // на главную и тост.
  const handleAddToCart = () => {
    impactOccuer('light');
    setOpenDetails(false);
    navigate('/');
    setTimeout(() => {
      custonSooner.success(t('successfully-added'));
    }, 100);
  };

  const handleShowQr = () => {
    impactOccuer('light');
    setOpenDetails(false);
    setOpenQr(true);
  };

  return (
    <>
      <div className="flex flex-col space-y-2">
        <PromotionCountdown endDate={deal.endDate} badgeOnly />
        <div
          className="flex flex-col space-y-3 rounded-[20px] bg-fill-base p-2 pb-4"
          onClick={() => setOpenDetails(true)}
        >
          <ProductImage
            src={imageUrl}
            alt="cover"
            fallbackSrc={evosLogo}
            placeholderClassName="bg-fill-background p-10"
            className="h-[200px] w-full rounded-xl object-cover"
          />
          <div className="flex flex-col space-y-3 px-2">
            <div className="flex flex-col space-y-1">
              <p className="text-body-base-medium text-text-primary">
                {title}
              </p>
              {description && <PromoMarkdown>{description}</PromoMarkdown>}
            </div>
            {SHOW_DEAL_ACTIONS && (
              <div className="flex flex-row items-center space-x-2">
                <Button
                  className="h-12 flex-1 rounded-full bg-button-secondary text-body-base-semibold text-text-primary shadow-none"
                  onClick={event => {
                    event.stopPropagation();
                    handleAddToCart();
                  }}
                >
                  {t('addToCart')}
                </Button>
                {deal.qrCodeUrl && (
                  <Button
                    className="h-12 w-[60px] shrink-0 rounded-full bg-button-secondary p-0 shadow-none"
                    onClick={event => {
                      event.stopPropagation();
                      handleShowQr();
                    }}
                  >
                    <QrIcon className="h-5 w-5 text-text-icon-primary" />
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <Drawer open={openDetails} onOpenChange={setOpenDetails}>
        <DrawerContent className="bg-fill-background pb-safe-bottom">
          <DrawerHeader
            title={t('promotion')}
            onClose={() => setOpenDetails(false)}
          />
          <div className="flex flex-col space-y-4 p-4 pb-8">
            <div className="flex flex-col space-y-4 rounded-[20px] bg-fill-base p-3">
              <ProductImage
                src={imageUrl}
                alt="cover"
                fallbackSrc={evosLogo}
                placeholderClassName="bg-fill-background p-10"
                className="w-full rounded-xl object-cover"
              />
              <div className="flex flex-col space-y-2">
                <p className="text-display-lg text-text-primary">
                  {title}
                </p>
                {/* В шторке вместо короткого описания — полное (условия
                    акции). Длинный текст листается вместе с ней: у
                    DrawerContent своя прокрутка до 85dvh. */}
                {fullDescription && (
                  <PromoMarkdown>{fullDescription}</PromoMarkdown>
                )}
              </div>
            </div>
            {SHOW_DEAL_ACTIONS && (
              <div className="flex flex-col space-y-2">
                {deal.qrCodeUrl && (
                  <Button
                    className="h-12 w-full space-x-2 rounded-full bg-brand-green text-body-base-semibold text-text-white shadow-none"
                    onClick={handleShowQr}
                  >
                    <QrIcon className="h-5 w-5 text-text-white" />
                    <span>{t('show-qr-btn')}</span>
                  </Button>
                )}
                <Button
                  className="h-12 w-full rounded-full bg-button-secondary text-body-base-semibold text-text-primary shadow-none"
                  onClick={handleAddToCart}
                >
                  {t('addToCart')}
                </Button>
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>

      {deal.qrCodeUrl && (
        <Drawer open={openQr} onOpenChange={setOpenQr}>
          <DrawerContent className="bg-fill-background pb-safe-bottom">
            <DrawerHeader
              title={t('qr-title')}
              onClose={() => setOpenQr(false)}
            />
            <div className="flex flex-col items-center space-y-4 p-4 pb-8">
              <p className="max-w-[240px] text-center text-caption-sm-medium text-text-secondary">
                {t('promo-qr')}
              </p>
              {/* QR акции бэкенд отдаёт готовой картинкой (qrCodeUrl). */}
              <div className="flex w-full items-center justify-center rounded-[28px] bg-fill-base p-6">
                <img
                  src={deal.qrCodeUrl}
                  alt="QR"
                  className="h-auto w-full max-w-[240px]"
                />
              </div>
            </div>
          </DrawerContent>
        </Drawer>
      )}
    </>
  );
};
